'use client'

/**
 * ChatThreadSidebar \u2014 Cursor-style thread history sidebar for chat v2.
 *
 * Renders inside the chat panel (left rail) when the v2 feature flag is
 * on. Shows pinned threads, recent threads, supports search, "New chat",
 * rename, pin/unpin, and delete.
 *
 * Per Stephen 2026-05-19: this replaces the "one infinite chat" UX. Each
 * chat session is a fresh thread; users browse history here.
 *
 * Spec: ~/.openclaw/workspace/specs/operation-sherman-spec.md \u00a74
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  PlusSignIcon,
  SearchIcon,
  PinLocation01Icon,
  PinLocation03Icon,
  MoreHorizontalIcon,
  Cancel01Icon,
  Edit02Icon,
  Delete02Icon,
} from '@hugeicons-pro/core-stroke-rounded'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export interface ChatThreadSidebarItem {
  id: string
  title: string
  updated_at: string
  pinned: boolean
  last_message: string | null
}

export interface ChatThreadSidebarProps {
  workspaceId: string | null
  mode: 'normal' | 'god'
  /** Currently-selected thread id, or null when the user is composing a new chat. */
  activeThreadId: string | null
  onSelectThread: (threadId: string) => void
  onNewChat: () => void
  /** Optional collapse handler for narrow viewports. */
  onClose?: () => void
  /** Refresh ticker \u2014 increment from the parent to force a refetch
   *  (e.g. after sending a message that may have updated a thread). */
  refreshKey?: number
}

const DEBOUNCE_MS = 200

export function ChatThreadSidebar({
  workspaceId,
  mode,
  activeThreadId,
  onSelectThread,
  onNewChat,
  onClose,
  refreshKey = 0,
}: ChatThreadSidebarProps) {
  const [threads, setThreads] = useState<ChatThreadSidebarItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<ChatThreadSidebarItem | null>(null)
  const renameInputRef = useRef<HTMLInputElement>(null)

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query])

  const fetchThreads = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (workspaceId) params.set('workspaceId', workspaceId)
      params.set('mode', mode)
      params.set('limit', '50')
      if (debouncedQuery) params.set('q', debouncedQuery)

      const res = await fetch(`/api/chat/threads?${params.toString()}`)
      if (!res.ok) {
        setError(`Failed to load threads (${res.status})`)
        setThreads([])
        return
      }
      const data = await res.json()
      const list = (data.threads ?? []) as ChatThreadSidebarItem[]
      setThreads(list)
    } catch (err) {
      setError((err as Error).message || 'Failed to load threads')
      setThreads([])
    } finally {
      setLoading(false)
    }
  }, [workspaceId, mode, debouncedQuery])

  useEffect(() => {
    void fetchThreads()
  }, [fetchThreads, refreshKey])

  // Optimistic helpers \u2014 keep UI responsive while server catches up.
  const patchThread = useCallback(
    async (id: string, patch: Partial<ChatThreadSidebarItem>) => {
      setThreads(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)))
      try {
        const res = await fetch(`/api/chat/threads/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        })
        if (!res.ok) {
          // Rollback by refetching authoritative state.
          await fetchThreads()
        }
      } catch {
        await fetchThreads()
      }
    },
    [fetchThreads],
  )

  const togglePin = useCallback(
    (t: ChatThreadSidebarItem) => {
      void patchThread(t.id, { pinned: !t.pinned })
    },
    [patchThread],
  )

  const startRename = useCallback((t: ChatThreadSidebarItem) => {
    setRenamingId(t.id)
    setRenameValue(t.title)
    requestAnimationFrame(() => {
      renameInputRef.current?.focus()
      renameInputRef.current?.select()
    })
  }, [])

  const commitRename = useCallback(async () => {
    if (!renamingId) return
    const title = renameValue.trim().slice(0, 80)
    setRenamingId(null)
    if (!title) return
    await patchThread(renamingId, { title })
  }, [renamingId, renameValue, patchThread])

  const cancelRename = useCallback(() => {
    setRenamingId(null)
  }, [])

  const deleteThread = useCallback(
    async (t: ChatThreadSidebarItem) => {
      setThreads(prev => prev.filter(x => x.id !== t.id))
      try {
        const res = await fetch(`/api/chat/threads/${t.id}`, { method: 'DELETE' })
        if (!res.ok) {
          await fetchThreads()
        }
      } catch {
        await fetchThreads()
      }
    },
    [fetchThreads],
  )

  const grouped = useMemo(() => {
    const pinned = threads.filter(t => t.pinned)
    const recent = threads.filter(t => !t.pinned)
    return { pinned, recent }
  }, [threads])

  return (
    <div className="flex h-full w-full flex-col bg-atrium-sidebar text-atrium-black">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-atrium-border px-3 py-2">
        <button
          type="button"
          onClick={onNewChat}
          className="flex flex-1 items-center gap-2 rounded-md border border-atrium-border bg-atrium-inner px-2.5 py-1.5 text-sm font-medium text-atrium-black transition-colors hover:bg-atrium-navlink-active"
        >
          <HugeiconsIcon icon={PlusSignIcon} className="size-4" />
          <span>New chat</span>
        </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close thread sidebar"
            className="rounded-md p-1.5 text-atrium-muted transition-colors hover:bg-atrium-navlink-active hover:text-atrium-black"
          >
            <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
          </button>
        )}
      </div>

      {/* Search */}
      <div className="px-3 py-2">
        <div className="relative">
          <HugeiconsIcon
            icon={SearchIcon}
            className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-atrium-muted"
          />
          <input
            type="text"
            placeholder="Search threads"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full rounded-md border border-atrium-border bg-atrium-inner py-1.5 pl-7 pr-2 text-sm text-atrium-black placeholder:text-atrium-muted focus:border-accent-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {loading && threads.length === 0 && (
          <div className="px-2 py-4 text-xs text-atrium-muted">Loading\u2026</div>
        )}
        {error && (
          <div className="px-2 py-4 text-xs text-red-600">{error}</div>
        )}
        {!loading && !error && threads.length === 0 && (
          <div className="px-2 py-6 text-center text-xs text-atrium-muted">
            {debouncedQuery ? 'No threads match.' : 'No conversations yet.'}
          </div>
        )}

        {grouped.pinned.length > 0 && (
          <SectionLabel label="Pinned" />
        )}
        {grouped.pinned.map(t => (
          <ThreadRow
            key={t.id}
            thread={t}
            active={t.id === activeThreadId}
            renaming={renamingId === t.id}
            renameValue={renameValue}
            onSelect={() => onSelectThread(t.id)}
            onTogglePin={() => togglePin(t)}
            onStartRename={() => startRename(t)}
            onChangeRenameValue={setRenameValue}
            onCommitRename={commitRename}
            onCancelRename={cancelRename}
            onAskDelete={() => setDeleteTarget(t)}
            renameInputRef={renameInputRef}
          />
        ))}

        {grouped.recent.length > 0 && grouped.pinned.length > 0 && (
          <SectionLabel label="Recent" />
        )}
        {grouped.recent.map(t => (
          <ThreadRow
            key={t.id}
            thread={t}
            active={t.id === activeThreadId}
            renaming={renamingId === t.id}
            renameValue={renameValue}
            onSelect={() => onSelectThread(t.id)}
            onTogglePin={() => togglePin(t)}
            onStartRename={() => startRename(t)}
            onChangeRenameValue={setRenameValue}
            onCommitRename={commitRename}
            onCancelRename={cancelRename}
            onAskDelete={() => setDeleteTarget(t)}
            renameInputRef={renameInputRef}
          />
        ))}
      </div>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={open => { if (!open) setDeleteTarget(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `\u201c${deleteTarget.title}\u201d and all its messages will be permanently deleted. This cannot be undone.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) {
                  const target = deleteTarget
                  setDeleteTarget(null)
                  void deleteThread(target)
                }
              }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function SectionLabel({ label }: { label: string }) {
  return (
    <div className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-atrium-muted">
      {label}
    </div>
  )
}

interface ThreadRowProps {
  thread: ChatThreadSidebarItem
  active: boolean
  renaming: boolean
  renameValue: string
  onSelect: () => void
  onTogglePin: () => void
  onStartRename: () => void
  onChangeRenameValue: (v: string) => void
  onCommitRename: () => void
  onCancelRename: () => void
  onAskDelete: () => void
  renameInputRef: React.RefObject<HTMLInputElement>
}

function ThreadRow({
  thread,
  active,
  renaming,
  renameValue,
  onSelect,
  onTogglePin,
  onStartRename,
  onChangeRenameValue,
  onCommitRename,
  onCancelRename,
  onAskDelete,
  renameInputRef,
}: ThreadRowProps) {
  const preview = thread.last_message?.slice(0, 80) ?? ''

  if (renaming) {
    return (
      <div className="group mx-0.5 my-0.5 rounded-md border border-accent-500 bg-atrium-inner px-2 py-1.5">
        <input
          ref={renameInputRef}
          value={renameValue}
          onChange={e => onChangeRenameValue(e.target.value)}
          onBlur={onCommitRename}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onCommitRename()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              onCancelRename()
            }
          }}
          className="w-full bg-transparent text-sm text-atrium-black focus:outline-none"
          maxLength={80}
        />
      </div>
    )
  }

  return (
    <div
      className={[
        'group relative mx-0.5 my-0.5 cursor-pointer rounded-md px-2 py-1.5 transition-colors',
        active
          ? 'bg-atrium-navlink-active'
          : 'hover:bg-atrium-navlink-active/60',
      ].join(' ')}
      onClick={onSelect}
    >
      <div className="flex items-start gap-1.5">
        {thread.pinned && (
          <HugeiconsIcon
            icon={PinLocation03Icon}
            className="mt-0.5 size-3 shrink-0 text-accent-500"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-atrium-black">
            {thread.title}
          </div>
          {preview && (
            <div className="truncate text-[11px] text-atrium-muted">
              {preview}
            </div>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={e => e.stopPropagation()}
              aria-label="Thread options"
              className="shrink-0 rounded-sm p-1 text-atrium-muted opacity-0 transition-opacity group-hover:opacity-100 hover:bg-atrium-border hover:text-atrium-black data-[state=open]:opacity-100"
            >
              <HugeiconsIcon icon={MoreHorizontalIcon} className="size-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={onTogglePin}>
              <HugeiconsIcon
                icon={thread.pinned ? PinLocation01Icon : PinLocation03Icon}
                className="mr-2 size-3.5"
              />
              {thread.pinned ? 'Unpin' : 'Pin'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onStartRename}>
              <HugeiconsIcon icon={Edit02Icon} className="mr-2 size-3.5" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onAskDelete}
              className="text-red-600 focus:text-red-700"
            >
              <HugeiconsIcon icon={Delete02Icon} className="mr-2 size-3.5" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
